import { request, resolveConfig, type ResolvedConfig } from "./http";
import type {
  AutomationInput,
  Campaign,
  ChatMedia,
  ChatServerTheme,
  ClientOptions,
  CreateLeadBulkResponse,
  CreateLeadInput,
  CreateLeadResponse,
  CreateMeetingInput,
  GetLeadHistoryParams,
  GetLeadHistoryResponse,
  GetLeadResponse,
  IsPausedResponse,
  Lead,
  ListTemplatesParams,
  MeetingNotesInput,
  RequestOptions,
  ResumeAutomationInput,
  SendMessageInput,
  SyncTagsInput,
  SyncTagsResponse,
  UUID,
  WhatsAppTemplate,
  Workflow,
} from "./types";

/** Señales de interaccion humana para el honeypot del servidor. Advisory.
 *  `inputs` es opcional: los SDK < 0.1.32 no lo mandan. */
export type InteractionSignal = {
  keys: number;
  inputs?: number;
  chars: number;
  cv: number | null;
  pasted: boolean;
};

const PUBLIC_PREFIX = "/api/public";
const WIDGET_PREFIX = "/api/widget/v1";

/** Proof-of-work solution attached to a chat turn body as `pow`. */
export type PowSolution = {
  challenge: string;
  salt: string;
  signature: string;
  number: number;
  expires: number;
};

/**
 * Low-level Nexor API client.
 *
 * Most users should call `init()` / `createClient()` instead of instantiating
 * this directly, but it's exported for advanced cases (DI, mocking).
 */
export class NexorClient {
  private cfg: ResolvedConfig;

  constructor(opts: ClientOptions) {
    this.cfg = resolveConfig(opts);
  }

  // ─── Leads ───────────────────────────────────────────────────────────────

  /** Create a single lead, optionally assigning it to a workflow. */
  createLead(
    input: CreateLeadInput,
    options?: RequestOptions,
  ): Promise<CreateLeadResponse> {
    return request<CreateLeadResponse>(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/leads`,
      body: { ...input, skip_first_message: true },
      options,
    });
  }

  /** Create many leads in one request (max 1000). */
  createLeadsBulk(
    inputs: CreateLeadInput[],
    options?: RequestOptions,
  ): Promise<CreateLeadBulkResponse> {
    return request<CreateLeadBulkResponse>(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/leads`,
      body: inputs.map((i) => ({ ...i, skip_first_message: true })),
      options,
    });
  }

  /** Update an existing lead. metadata is shallow-merged server-side. */
  updateLead(
    leadId: UUID,
    updates: Partial<CreateLeadInput>,
    options?: RequestOptions,
  ): Promise<{ success: true; lead: Lead }> {
    return request(this.cfg, {
      method: "PUT",
      path: `${PUBLIC_PREFIX}/leads/${encodeURIComponent(leadId)}`,
      body: updates,
      options,
    });
  }

  /** Fetch a lead with its captured variables and per-channel engagement. */
  getLead(leadId: UUID, options?: RequestOptions): Promise<GetLeadResponse> {
    return request<GetLeadResponse>(this.cfg, {
      method: "GET",
      path: `${PUBLIC_PREFIX}/leads/${encodeURIComponent(leadId)}`,
      options,
    });
  }

  /** Full chronological history: messages, transcripts, activity. */
  getLeadHistory(
    leadId: UUID,
    params: GetLeadHistoryParams = {},
    options?: RequestOptions,
  ): Promise<GetLeadHistoryResponse> {
    const channel = Array.isArray(params.channel)
      ? params.channel
      : params.channel
        ? [params.channel]
        : undefined;
    return request<GetLeadHistoryResponse>(this.cfg, {
      method: "GET",
      path: `${PUBLIC_PREFIX}/leads/${encodeURIComponent(leadId)}/history`,
      query: { channel, limit: params.limit, offset: params.offset },
      options,
    });
  }

  /** Has automation been paused (human takeover) for this lead? */
  isLeadPaused(
    leadId: UUID,
    params: { workflow_id?: UUID } = {},
    options?: RequestOptions,
  ): Promise<IsPausedResponse> {
    return request<IsPausedResponse>(this.cfg, {
      method: "GET",
      path: `${PUBLIC_PREFIX}/leads/${encodeURIComponent(leadId)}/is_paused`,
      query: { workflow_id: params.workflow_id },
      options,
    });
  }

  /** Pause automation (engage human takeover) on a specific workflow run. */
  stopAutomation(
    leadId: UUID,
    input: AutomationInput,
    options?: RequestOptions,
  ): Promise<unknown> {
    return request(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/leads/${encodeURIComponent(leadId)}/automation/stop`,
      body: input,
      options,
    });
  }

  /** Resume automation; pass resume_cadence: true to restart scheduled touches. */
  resumeAutomation(
    leadId: UUID,
    input: ResumeAutomationInput,
    options?: RequestOptions,
  ): Promise<unknown> {
    return request(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/leads/${encodeURIComponent(leadId)}/automation/resume`,
      body: input,
      options,
    });
  }

  /**
   * Replace the tag set on a lead (identified by lead_id OR email).
   * Tags missing from the input are unassigned; new tags are find-or-created.
   */
  syncLeadTags(
    input: SyncTagsInput,
    options?: RequestOptions,
  ): Promise<SyncTagsResponse> {
    return request<SyncTagsResponse>(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/leads/tags`,
      body: input,
      options,
    });
  }

  /** All meetings on file for a lead (optionally filter by status). */
  listLeadMeetings(
    leadId: UUID,
    params: { status?: string | string[] } = {},
    options?: RequestOptions,
  ): Promise<unknown> {
    const status = Array.isArray(params.status)
      ? params.status.join(",")
      : params.status;
    return request(this.cfg, {
      method: "GET",
      path: `${PUBLIC_PREFIX}/leads/${encodeURIComponent(leadId)}/meetings`,
      query: { status },
      options,
    });
  }

  // ─── Workflows / Campaigns / Templates ──────────────────────────────────

  /** Active workflows in your account. */
  listWorkflows(
    options?: RequestOptions,
  ): Promise<{ success: true; workflows: Workflow[] }> {
    return request(this.cfg, {
      method: "GET",
      path: `${PUBLIC_PREFIX}/workflows`,
      options,
    });
  }

  /** Campaigns in your account. */
  listCampaigns(
    options?: RequestOptions,
  ): Promise<{ success: true; campaigns: Campaign[] }> {
    return request(this.cfg, {
      method: "GET",
      path: `${PUBLIC_PREFIX}/campaigns`,
      options,
    });
  }

  /** Approved WhatsApp templates. */
  listTemplates(
    params: ListTemplatesParams = {},
    options?: RequestOptions,
  ): Promise<{ success: true; templates: WhatsAppTemplate[] }> {
    return request(this.cfg, {
      method: "GET",
      path: `${PUBLIC_PREFIX}/templates`,
      query: { status: params.status, category: params.category },
      options,
    });
  }

  // ─── Messages ───────────────────────────────────────────────────────────

  /** Send a one-off message (WhatsApp/email) or trigger a call. */
  sendMessage(
    input: SendMessageInput,
    options?: RequestOptions,
  ): Promise<unknown> {
    return request(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/messages`,
      body: input,
      options,
    });
  }

  // ─── Meetings ───────────────────────────────────────────────────────────

  /** Log a booked meeting against an existing lead (matched by email). */
  createMeeting(
    input: CreateMeetingInput,
    options?: RequestOptions,
  ): Promise<unknown> {
    return request(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/meetings`,
      body: input,
      options,
    });
  }

  /** Push a note-taker transcript / summary / action items to a meeting. */
  createMeetingNotes(
    input: MeetingNotesInput,
    options?: RequestOptions,
  ): Promise<unknown> {
    return request(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/meeting-notes`,
      body: input,
      options,
    });
  }

  // ─── Chat (used by initChat widget) ─────────────────────────────────────

  /**
   * Fetch the dashboard-authored web-chat config for a workflow. Backed by
   * GET /api/widget/v1/config (embedded) or GET /api/public/chat/config. The
   * widget calls this once on init to learn whether web chat is enabled and to
   * get the static opening message (`initial_message`, a RAW template that may
   * contain {{variables}} — the widget substitutes them client-side before
   * painting the greeting) plus the dashboard-authored `theme`.
   *
   * MUST branch on `embedded` the same way `getPowChallenge`/turns already do:
   * the `/api/public/chat*` prefix's CORS preflight is blocked for the
   * chat.getnexor.ai iframe origin (Cloudflare WAF, 2026-07-23 — Aliace theme
   * silently never applied because this call always hit the public prefix even
   * when embedded, while turns correctly used `/api/widget/v1/turn`). The
   * widget-scoped endpoint returns the same `{enabled, initial_message, theme}`
   * shape and additionally honors `parent_origin` for allowed-origins gating.
   *
   * Most users will not call this directly — `initChat()` handles it.
   */
  getChatConfig(
    workflowId: UUID,
    opts?: {
      embedded?: boolean;
      parentOrigin?: string;
      options?: RequestOptions;
    },
  ): Promise<{
    /** Only the PUBLIC surface sends this; the widget-scoped response has no
     *  `success` field at all — don't rely on it being present. */
    success?: true;
    enabled: boolean;
    /** Widget-scoped surface only: why `enabled` is false (e.g.
     *  "channel_disabled" | "origin_not_allowed"). Absent on the public surface. */
    reason?: string | null;
    initial_message: string | null;
    /** Dashboard-authored branding/behaviour, applied on top of the widget's
     *  defaults but BELOW anything the embedder passed explicitly to
     *  `initChat()`. All fields optional. */
    theme?: ChatServerTheme;
  }> {
    const path = opts?.embedded
      ? `${WIDGET_PREFIX}/config`
      : `${PUBLIC_PREFIX}/chat/config`;
    return request(this.cfg, {
      method: "GET",
      path,
      query: {
        workflow_id: workflowId,
        ...(opts?.embedded && opts?.parentOrigin
          ? { parent_origin: opts.parentOrigin }
          : {}),
      },
      options: opts?.options,
    });
  }

  /**
   * Fetch a fresh proof-of-work challenge to attach to the next chat turn.
   * Backed by GET /api/widget/v1/pow-challenge (embedded) or
   * GET /api/public/chat/pow-challenge. The challenge is signed with the
   * server's POW_SECRET and does NOT depend on the workflow, so no query is
   * sent. Each challenge is single-use (anti-replay) — mint one per turn.
   */
  getPowChallenge(
    _workflowId: UUID,
    opts?: { embedded?: boolean; options?: RequestOptions },
  ): Promise<{
    success: true;
    algorithm: string;
    challenge: string;
    salt: string;
    signature: string;
    maxnumber: number;
    expires: number;
  }> {
    const prefix = opts?.embedded ? WIDGET_PREFIX : PUBLIC_PREFIX;
    const path = opts?.embedded
      ? `${prefix}/pow-challenge`
      : `${prefix}/chat/pow-challenge`;
    return request(this.cfg, {
      method: "GET",
      path,
      options: opts?.options,
    });
  }

  /**
   * Send a turn in a browser-embedded chat session. Backed by
   * POST /api/public/chat on the Nexor API.
   *
   * Most users will not call this directly — `initChat()` handles it.
   */
  chat(
    input: {
      workflow_id: UUID;
      session_id: string;
      message: string;
      system_prompt?: string;
      client_prompt?: string;
      /** The opening message the widget actually displayed (keeps the agent's
       *  <webchat_opening> in sync with a per-page override). */
      opening_shown?: string;
      /** Start over as a brand-new lead: the API skips email/phone/session
       *  matching and mints a fresh lead with no prior conversation context. */
      new_lead?: boolean;
      /** Keep the same lead but start a fresh conversation episode: the API
       *  deactivates the prior run + thread and clears this workflow's collected
       *  variables, so the agent sees no prior context. */
      reset_episode?: boolean;
      lead?: {
        first_name?: string;
        last_name?: string;
        email?: string;
        phone?: string;
        metadata?: Record<string, unknown>;
      };
      metadata?: Record<string, unknown>;
      /** Human-interaction signals for the server-side honeypot. Advisory. */
      interaction?: InteractionSignal;
      /** Proof-of-work solution for the anti-bot gate. */
      pow?: PowSolution;
    },
    options?: RequestOptions,
  ): Promise<{
    success: true;
    reply: string;
    session_id: string;
    lead_id?: UUID;
    workflow_run_id?: UUID;
    /** True when the API recognised an existing lead (returning visitor). */
    matched_existing?: boolean;
    /** File(s) the AI attached to this reply (inline media channels). */
    media?: ChatMedia[];
  }> {
    return request(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/chat`,
      body: input,
      options,
    });
  }

  /**
   * Send a turn in an IFRAME-embedded chat session, origin-bound. Backed by
   * POST /api/widget/v1/turn — the surface that validates the proof-of-work
   * solution AND `parent_origin` against the workflow's
   * allowed origins. `initChat()` calls this instead of `chat()` when the
   * widget detects it's running inside an iframe.
   *
   * The widget turn schema is stricter than `chat()`'s: no `system_prompt`,
   * and `lead` carries no `metadata`.
   *
   * Most users will not call this directly — `initChat()` handles it.
   */
  widgetTurn(
    input: {
      workflow_id: UUID;
      session_id: string;
      message: string;
      client_prompt?: string;
      opening_shown?: string;
      new_lead?: boolean;
      reset_episode?: boolean;
      lead?: {
        first_name?: string;
        last_name?: string;
        email?: string;
        phone?: string;
      };
      metadata?: Record<string, unknown>;
      /** Human-interaction signals for the server-side honeypot (keystroke
       *  count, timing coefficient of variation, paste flag). Advisory. */
      interaction?: InteractionSignal;
      /** The real parent-page origin (`window.location.ancestorOrigins[0]`,
       *  not falsifiable by the embedding page's JS), checked against the
       *  workflow's allowed origins. */
      parent_origin?: string;
      /** Proof-of-work solution for the anti-bot gate. */
      pow?: PowSolution;
    },
    options?: RequestOptions,
  ): Promise<{
    success: true;
    reply: string;
    session_id: string;
    lead_id?: UUID;
    workflow_run_id?: UUID;
    matched_existing?: boolean;
    media?: ChatMedia[];
  }> {
    return request(this.cfg, {
      method: "POST",
      path: `${WIDGET_PREFIX}/turn`,
      body: input,
      options,
    });
  }

  /**
   * Create (or upsert) a lead from the web chat widget and force its FIRST
   * contact to go out on a chosen channel — used when a visitor clicks
   * "call me" / "email me" instead of opening the chat. Backed by
   * POST /api/public/leads with `force_first_channel`.
   *
   * Unlike a literal `force_first_message`, `force_first_channel` pins the
   * channel and lets the workflow's own agent compose the first outreach. The
   * `metadata` you pass (page URL, requested channel, a note that the visitor
   * asked to be contacted from the website) is stored on the lead so the agent
   * has that context.
   *
   * Most users will not call this directly — `initChat()`'s contact-request
   * form handles it.
   */
  requestContact(
    input: {
      workflow_id: UUID;
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      /** Force the first contact onto this channel (agent composes the message). */
      force_first_channel: "call" | "email" | "whatsapp";
      /** Context stored on the lead (page URL, requested channel, free-form note). */
      metadata?: Record<string, unknown>;
      /** Lead source label. Defaults to "web-chat" server-side handling. */
      source?: string;
    },
    options?: RequestOptions,
  ): Promise<{
    success: true;
    lead: { id: UUID; first_name?: string; email?: string; phone?: string };
    existed?: boolean;
    workflow_run?: { id: UUID };
    force_first_channel?: { channel: string; applied: boolean };
    warning?: string;
  }> {
    return request(this.cfg, {
      method: "POST",
      path: `${PUBLIC_PREFIX}/leads`,
      body: input,
      options,
    });
  }
}
